// A TELA DE ÓRGÃOS E ENTIDADES — o cadastro de tudo que paga folha (feature 009).
//
// Era `prefeituras.tsx`, e o nome não era detalhe: a lista já guardava câmaras,
// autarquias e institutos chamando todos de prefeitura, e o operador não tinha
// como separar os três órgãos de um município no meio de duzentas linhas de
// municípios diferentes. O que muda aqui:
//
//   1. OS FILTROS VÃO NA URL. Antes eram `useState`: o recorte morria no F5 e
//      não dava para mandar "olha estas duas autarquias" por link — que é como
//      o onboarding conversa. Agora `?tipo=autarquia&municipio=…` é o endereço
//      da pergunta.
//   2. DOIS AGRUPAMENTOS, porque são duas perguntas. Por responsável responde
//      "quem opera por quem"; por município responde "o que existe nesta
//      cidade". Nenhum dos dois serve para as duas — e até aqui só o primeiro
//      existia, sem controle na tela para trocar.
//   3. A LINHA ABRE A FICHA. O cadastro deixou de caber num modal: convênios,
//      folha própria, usuários e (em US2) relações institucionais são do mesmo
//      órgão e viviam em quatro telas distintas. Ver `organizacao-ficha.tsx`.
//
// O modal de cadastro saiu para `organizacao-modal.tsx` sem mudar de
// comportamento — a ficha precisa exatamente dele, e duas cópias divergiriam na
// primeira correção feita só de um lado.
import { useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Button,
  DataTable,
  FilterBar,
  IconButton,
  Pill,
  SelectField,
  formatCnpj,
  type Column,
} from "@atlas/ui/web";
import { atlas } from "../../lib/sdk";
import { filtrarOrdenarCadastros } from "../../lib/filtro-cadastros";
import { ConfirmarRecusaModal } from "../../components/ConfirmarRecusaModal";
import { AcoesCadastroPendente } from "../../components/AcoesCadastroPendente";
import { LayoutChip } from "../../components/LayoutChip";
import { DocumentosPill } from "../../components/DocumentosCadastro";
import { OrganizacaoModal, type ModoModal } from "./organizacao-modal";
import { ESFERA_OPCOES, TIPO_ORGANIZACAO_OPCOES, rotuloTipo } from "./organizacao-campos";
import { ROTULOS, type Esfera, type TipoOrganizacao } from "@atlas/types";
import type { AdminPrefeitura } from "@atlas/sdk";

/**
 * Cada responsável seguido das organizações que ele atende — a ordem que faz a
 * indentação da coluna Nome significar algo. Atendida cujo responsável não está
 * na lista (desativado e filtrado, por exemplo) vai pro fim em vez de sumir.
 *
 * A CHAVE DEIXOU DE SER `prefeituraPaiId` (T060). Ele dizia uma coisa só, sem
 * papel e sem vigência: a autarquia que trocou de processador em setembro
 * continuava aparecendo sob quem a processava em janeiro, porque a coluna não
 * tinha como saber que a decisão mudou. Agora o pai de cada linha é quem
 * `processa_folha` dela NA COMPETÊNCIA CORRENTE — a mesma fonte do hub de
 * layouts desde T046.
 *
 * `responsavelDe` é injetado para a função continuar pura: quem a chama já leu
 * as relações, e um agrupamento que faz I/O é um agrupamento que não se testa.
 */
export function agruparPorResponsavel(
  todas: AdminPrefeitura[],
  responsavelDe: (unidadeId: number) => number | null,
): AdminPrefeitura[] {
  const atendidasDe = new Map<number, AdminPrefeitura[]>();
  const semResponsavel: AdminPrefeitura[] = [];
  for (const p of todas) {
    const resp = responsavelDe(p.id);
    if (resp == null) semResponsavel.push(p);
    else atendidasDe.set(resp, [...(atendidasDe.get(resp) ?? []), p]);
  }
  const ordenadas = semResponsavel.flatMap((r) => [r, ...(atendidasDe.get(r.id) ?? [])]);
  const vistas = new Set(ordenadas.map((p) => p.id));
  return [...ordenadas, ...todas.filter((p) => !vistas.has(p.id))];
}

/**
 * Agrupamento alternativo: por município.
 *
 * Município `0` (órgão estadual, ou cadastro do site ainda sem o código IBGE)
 * vai para o fim, agrupado, em vez de se misturar com o município de código zero
 * que não existe.
 */
export function agruparPorMunicipio(todas: AdminPrefeitura[]): AdminPrefeitura[] {
  const chave = (p: AdminPrefeitura) => p.municipioIbge || 0;
  const semMunicipio = todas.filter((p) => !chave(p));
  const comMunicipio = todas.filter((p) => chave(p));
  const porCodigo = new Map<number, AdminPrefeitura[]>();
  for (const p of comMunicipio) {
    const k = chave(p);
    porCodigo.set(k, [...(porCodigo.get(k) ?? []), p]);
  }
  const rotulo = (p: AdminPrefeitura) => (p.municipioNome || p.nome || "").toLocaleLowerCase("pt-BR");
  const grupos = [...porCodigo.values()].sort((a, b) => rotulo(a[0]!).localeCompare(rotulo(b[0]!), "pt-BR"));
  return [...grupos.flat(), ...semMunicipio];
}

export function AdminOrganizacoes() {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  // Mesma queryKey de sempre (`admin/prefeituras`): o modal e a esteira de
  // pendentes invalidam por ela, e trocar a chave junto com o nome do arquivo
  // deixaria a lista parada depois de salvar.
  // `incluirTeste` entra na queryKey: sem ele, o TanStack serviria a lista
  // sem os tenants de teste do cache quando o operador ligasse o filtro.
  const incluirTesteParam = params.get("incluirTeste") === "1";
  const data = useQuery({
    queryKey: ["admin", "prefeituras", incluirTesteParam],
    queryFn: () => atlas.admin.listPrefeituras(incluirTesteParam ? { incluirTeste: "1" } : undefined),
  });
  // QUEM PROCESSA A FOLHA DE QUEM, na competência corrente (T060). Sem filtro
  // de competência a chamada já responde "o que vale hoje", que é a pergunta da
  // tela ao abrir. Falha ou carregamento devolvem mapa vazio: a lista continua
  // legível, plana, em vez de não abrir por causa do agrupamento.
  const relacoes = useQuery({
    queryKey: ["admin", "relacoes", "processa_folha"],
    queryFn: () => atlas.admin.relacoes.list({ papel: "processa_folha", situacao: "vigente" }),
  });
  const responsavelDe = useMemo(() => {
    const mapa = new Map<number, number>();
    for (const r of relacoes.data?.relacoes ?? []) mapa.set(r.atendidaId, r.responsavelId);
    // A organização que processa a PRÓPRIA folha não tem responsável: `null` é
    // "ela mesma", e é o que a põe no nível de cima do agrupamento.
    return (unidadeId: number) => mapa.get(unidadeId) ?? null;
  }, [relacoes.data]);

  // O RECORTE MORA NA URL. `pendentes=1` é a entrada da esteira do site — o
  // badge do menu aponta para ela, e ela precisa sobreviver a um F5 e a um link
  // colado no chat do suporte.
  const filtros = {
    q: params.get("q") ?? "",
    tipo: params.get("tipo") ?? "",
    esfera: params.get("esfera") ?? "",
    situacao: params.get("situacao") ?? "",
    folhaPropria: params.get("folhaPropria") ?? "",
    municipio: params.get("municipio") ?? "",
    pendentes: params.get("pendentes") === "1",
    // Organizacao de teste some por padrao — item 9 do PENDENCIAS.md. O filtro
    // e' o caminho de volta: alguem precisa administrar o cadastro, e um
    // registro que nao se alcanca e' pior que um que conta errado.
    incluirTeste: params.get("incluirTeste") === "1",
    agrupar: params.get("agrupar") === "municipio" ? "municipio" : "responsavel",
  };
  const trocar = (patch: Record<string, string>) => {
    const p = new URLSearchParams(params);
    for (const [k, v] of Object.entries(patch)) {
      if (v) p.set(k, v);
      else p.delete(k);
    }
    setParams(p, { replace: true });
  };
  const limpar = () =>
    trocar({ q: "", tipo: "", esfera: "", situacao: "", folhaPropria: "", municipio: "", pendentes: "", incluirTeste: "" });
  const temRecorte = !!(filtros.q || filtros.tipo || filtros.esfera || filtros.situacao
    || filtros.folhaPropria || filtros.municipio || filtros.pendentes || filtros.incluirTeste);

  const sync = useMutation({
    mutationFn: (id: number) => atlas.admin.sincronizarPrefeitura(id),
    onSuccess: (r) => {
      qc.invalidateQueries({ queryKey: ["admin", "prefeituras"] });
      const res = r.resultado;
      if (res.erro) alert(`Sincronização com aviso:\n${res.erro}`);
      else alert(`Sincronizado: ${res.novos} novos, ${res.atualizados} atualizados.`);
    },
    onError: (err) => alert(err instanceof Error ? err.message : "Falha ao sincronizar"),
  });
  // Nunca exclui — desativa/reativa (status). Reversível, sem perda de dados.
  const toggleAtivo = useMutation({
    mutationFn: (p: AdminPrefeitura) => atlas.admin.upsertPrefeitura({
      id: p.id, nome: p.nome, uf: p.uf, municipioIbge: p.municipioIbge,
      modoIntegracao: p.modoIntegracao, status: p.status === "inativo" ? "ativo" : "inativo",
      // Sem reenviar o vinculo, um toggle numa filial a promoveria a raiz.
      prefeituraPaiId: p.prefeituraPaiId ?? null,
      loginEmail: p.loginEmail, servidoresCount: p.servidoresCount,
      distribuicaoAutomaticaDescontos: p.distribuicaoAutomaticaDescontos,
    }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["admin", "prefeituras"] }),
  });
  const recusar = useMutation({
    mutationFn: ({ id, motivo }: { id: number; motivo: string }) => atlas.admin.recusarCadastroPrefeitura(id, motivo),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["admin", "prefeituras"] });
      // Badge do menu decrementa junto com a recusa.
      qc.invalidateQueries({ queryKey: ["admin", "cadastros-pendentes"] });
    },
  });

  // `modo` distingue a edição normal da conferência de aprovação — mesmo modal,
  // rodapé e cabeçalho diferentes.
  const [editing, setEditing] = useState<{ prefeitura: AdminPrefeitura | null; modo: ModoModal } | null>(null);
  const [recusando, setRecusando] = useState<AdminPrefeitura | null>(null);

  const todas = data.data?.prefeituras ?? [];
  const porId = new Map(todas.map((p) => [p.id, p]));

  // Busca por nome/sigla/razão social/CNPJ + pendentes no topo, resto A-Z.
  const rows = filtrarOrdenarCadastros(todas, filtros.q).filter((p) => {
    if (filtros.pendentes && p.status !== "pendente") return false;
    if (filtros.tipo && (p.tipo ?? "prefeitura") !== filtros.tipo) return false;
    if (filtros.esfera && (p.esfera ?? "municipal") !== filtros.esfera) return false;
    if (filtros.situacao && p.status !== filtros.situacao) return false;
    if (filtros.municipio && String(p.municipioIbge) !== filtros.municipio) return false;
    // `temFolhaPropria` é derivado pela API (tem servidor, convênio ou
    // competência). AUSENTE não é `false`: uma resposta de API anterior ao campo
    // não deve sumir da lista de quem filtrou por "processa" — some só quem a
    // API afirmou que não processa.
    if (filtros.folhaPropria === "sim" && p.temFolhaPropria === false) return false;
    if (filtros.folhaPropria === "nao" && p.temFolhaPropria !== false) return false;
    return true;
  });

  // O seletor de município sai da BASE INTEIRA, não do recorte: uma lista de
  // municípios que encolhe conforme se filtra impede justamente o movimento de
  // trocar de município sem limpar tudo antes.
  const municipios = (() => {
    const vistos = new Map<string, string>();
    for (const p of todas) {
      if (!p.municipioIbge) continue;
      vistos.set(String(p.municipioIbge), `${p.municipioNome || p.nome}${p.uf ? `/${p.uf}` : ""}`);
    }
    return [...vistos.entries()].sort((a, b) => a[1].localeCompare(b[1], "pt-BR"));
  })();

  // Colunas: as operacionais que a tela sempre teve (CNPJ, telefone, layouts,
  // servidores, sync) mais o que a 009 acrescenta — a sigla junto do nome, a
  // esfera junto do tipo e a coluna Folha. Poder e classificação NÃO viraram
  // duas colunas novas de propósito: a tabela já é larga, tipo + esfera bastam
  // para a triagem, e o resto da identidade está na ficha.
  const columns: Column<AdminPrefeitura>[] = [
    { key: "status", header: "Situação", render: (p) => <Pill variant={p.status === "ativo" ? "averbado" : p.status === "inativo" ? "expirado" : "pendente"}>{p.status}</Pill> },
    {
      key: "nome",
      header: "Nome",
      // Atendida aparece INDENTADA sob quem processa a folha dela. Antes, três
      // unidades do mesmo município eram três linhas soltas e nada na tabela
      // dizia que eram do mesmo cliente — o parentesco vivia na cabeça de quem
      // cadastrou. E a indentação segue a RELAÇÃO desde T060: quem trocou de
      // processador muda de lugar no mês em que a troca vale.
      render: (p) => (
        <span style={{ display: "inline-block", paddingLeft: responsavelDe(p.id) != null ? 18 : 0 }}>
          {responsavelDe(p.id) != null ? <span style={{ color: "var(--text-dim)", marginRight: 6 }}>↳</span> : null}
          <Link to={`/averbadora/organizacoes/${p.id}`} style={{ color: "inherit", fontWeight: 600 }}>
            {p.nome}
          </Link>
          {p.sigla ? (
            <span style={{ display: "block", fontSize: 11, color: "var(--text-dim)", fontFamily: "var(--font-mono)" }}>{p.sigla}</span>
          ) : null}
        </span>
      ),
    },
    {
      key: "tipo",
      header: "Tipo",
      render: (p) => (
        <div style={{ display: "grid", gap: 2 }}>
          <span style={{ fontSize: 12 }}>{p.rotuloTipo ?? rotuloTipo(p.tipo as TipoOrganizacao | undefined)}</span>
          <span style={{ fontSize: 11, color: "var(--text-dim)" }}>
            {ROTULOS.esfera[(p.esfera ?? "municipal") as Esfera]}
            {p.municipioNome ? ` · ${p.municipioNome}` : ""}
          </span>
        </div>
      ),
    },
    { key: "uf", header: "UF", mono: true },
    {
      key: "municipioIbge",
      header: "Código IBGE",
      mono: true,
      render: (p) => p.municipioIbge > 0 ? p.municipioIbge : <span style={{ color: "var(--danger-500)" }}>—</span>,
    },
    {
      key: "cnpj",
      header: "CNPJ",
      mono: true,
      render: (p) => p.cnpj ? formatCnpj(p.cnpj) : <span style={{ color: "var(--text-dim)" }}>—</span>,
    },
    {
      key: "folha",
      header: "Folha",
      // TRÊS ESTADOS, e o terceiro é o que a coluna existe para mostrar: um
      // órgão que não processa nada não deveria receber corte, partes nem
      // layout, e até aqui nada na lista dizia isso.
      render: (p) => {
        // QUEM PROCESSA, e não quem é mãe (T060): a coluna passou a ler a
        // relação vigente. A autarquia que trocou de processador em setembro
        // dizia "por" quem a processava em janeiro — a resposta certa para uma
        // pergunta que ninguém fez.
        const respId = responsavelDe(p.id);
        const responsavel = respId != null ? porId.get(respId) : undefined;
        if (responsavel) {
          return (
            <span style={{ fontSize: 12, color: "var(--text-muted)" }} title={`Processada dentro da folha de ${responsavel.nome}.`}>
              por {responsavel.nome}
            </span>
          );
        }
        if (p.temFolhaPropria === false) {
          return (
            <span style={{ color: "var(--text-dim)" }} title="Sem servidor, convênio ou competência — esta organização não processa folha própria.">
              não processa
            </span>
          );
        }
        return <span style={{ fontSize: 12, color: "var(--emerald-500)" }}>própria</span>;
      },
    },
    { key: "modoIntegracao", header: "Integração" },
    {
      key: "acesso",
      header: "Login",
      render: (p) => p.loginEmail
        ? <span style={{ color: "var(--emerald-500)" }}>{p.loginEmail}</span>
        : <span style={{ color: "var(--text-dim)" }}>—</span>,
    },
    {
      key: "senha",
      header: "Acesso",
      render: (p) => !p.loginEmail
        ? <span style={{ color: "var(--text-dim)" }}>—</span>
        : p.hasPassword
          ? <span style={{ color: "var(--emerald-500)" }} title="O master já criou a senha no primeiro acesso">✓ ativado</span>
          : <span style={{ color: "var(--gold-500)" }} title="O master ainda não fez o primeiro acesso">aguardando primeiro acesso</span>,
    },
    {
      key: "telefone",
      header: "Telefone",
      render: (p) => p.telefone || <span style={{ color: "var(--text-dim)" }}>—</span>,
    },
    // Anexos do cadastro do site. Na lista importa saber se há o que conferir
    // ANTES de abrir o modal — sobretudo na fila de pendentes.
    {
      key: "documentos",
      header: "Documentos",
      render: (p) => <DocumentosPill docFrenteKey={p.docFrenteKey} docVersoKey={p.docVersoKey} selfieKey={p.selfieKey} />,
    },
    {
      key: "layouts",
      header: "Layout",
      // A COLUNA SÓ FALA QUANDO HÁ MOTIVO. Quase toda unidade está no padrão
      // Atlas, e "padrão / padrão" repetido em 40 linhas seria ruído. O que
      // precisa saltar é a exceção — e é ela que o link leva a conferir.
      render: (p) => {
        if (!p.layouts) return <span style={{ color: "var(--text-dim)" }}>—</span>;
        return (
          <Link
            to={`/averbadora/layouts/unidade/${p.id}`}
            style={{ display: "grid", gap: 4, textDecoration: "none", color: "inherit" }}
            title={`Retorno: ${p.layouts.entrada.codigo} · Movimento: ${p.layouts.saida.codigo}`}
            aria-label={`Abrir os layouts da unidade ${p.nome}`}
          >
            {(["entrada", "saida"] as const).map((s) => {
              const l = p.layouts![s];
              return <LayoutChip key={s} tamanho="sm" nome={l.nome} codigo={l.codigo} versao={l.versao} situacao={l.situacao} padrao={l.padrao} emTeste={p.deProva === true && l.situacao === "rascunho"} />;
            })}
          </Link>
        );
      },
    },
    { key: "servidoresCount", header: "Servidores", align: "right", render: (p) => p.servidoresCount.toLocaleString("pt-BR") },
    {
      key: "ultimaSincronizacao",
      header: "Última sync",
      render: (p) => (p.ultimaSincronizacao ? new Date(p.ultimaSincronizacao).toLocaleString("pt-BR") : "—"),
    },
  ];

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
      <header style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <div>
          <span style={{ fontSize: 12, letterSpacing: "0.1em", fontWeight: 700, color: "var(--text-dim)", textTransform: "uppercase" }}>
            Averbadora
          </span>
          <h1 style={{ margin: "4px 0 0", fontSize: "1.6rem" }}>Órgãos e entidades</h1>
        </div>
        <div style={{ display: "flex", gap: 8 }}>
          {/* Botão "Limpar Base" removido — operação destrutiva demais pra
              expor no header. Continua acessível via /averbadora/manutencao
              (permissão manutencao) ou POST direto na API. */}
          <Button onClick={() => setEditing({ prefeitura: null, modo: "editar" })}>+ Novo órgão ou entidade</Button>
        </div>
      </header>

      <FilterBar
        searchValue={filtros.q}
        onSearchChange={(v) => trocar({ q: v })}
        searchPlaceholder="Buscar por nome, sigla, CNPJ ou razão social"
        onReset={limpar}
      >
        <SelectField
          label="Tipo"
          value={filtros.tipo}
          onChange={(e) => trocar({ tipo: e.target.value })}
          options={[{ value: "", label: "Todos os tipos" }, ...TIPO_ORGANIZACAO_OPCOES]}
        />
        <SelectField
          label="Esfera"
          value={filtros.esfera}
          onChange={(e) => trocar({ esfera: e.target.value })}
          options={[{ value: "", label: "Todas as esferas" }, ...ESFERA_OPCOES]}
        />
        <SelectField
          label="Situação"
          value={filtros.situacao}
          onChange={(e) => trocar({ situacao: e.target.value })}
          options={[
            { value: "", label: "Todas as situações" },
            { value: "ativo", label: "Ativo" },
            { value: "pausado", label: "Pausado" },
            { value: "inativo", label: "Inativo" },
            { value: "pendente", label: "Pendente (esteira do site)" },
          ]}
        />
        <SelectField
          label="Folha própria"
          value={filtros.folhaPropria}
          onChange={(e) => trocar({ folhaPropria: e.target.value })}
          options={[
            { value: "", label: "Tanto faz" },
            { value: "sim", label: "Processa folha própria" },
            { value: "nao", label: "Não processa" },
          ]}
        />
        {/* Nao e' "situacao": todo valor nao-ativo de `status` barra o LOGIN, e a
            equipe precisa entrar no proprio tenant de teste. Sao eixos
            diferentes — status e' acesso, este e' contagem. */}
        <SelectField
          label="Organizações de teste"
          value={filtros.incluirTeste ? "1" : ""}
          onChange={(e) => trocar({ incluirTeste: e.target.value })}
          options={[
            { value: "", label: "Esconder (padrão)" },
            { value: "1", label: "Mostrar junto" },
          ]}
        />
        <SelectField
          label="Município"
          value={filtros.municipio}
          onChange={(e) => trocar({ municipio: e.target.value })}
          options={[{ value: "", label: "Todos os municípios" }, ...municipios.map(([v, l]) => ({ value: v, label: l }))]}
        />
        {/* AGRUPAR NÃO É FILTRAR: as duas perguntas ("quem opera por quem" e "o
            que existe nesta cidade") veem a mesma lista em ordens diferentes.
            Fica na barra porque é onde o operador já está mexendo no recorte. */}
        <SelectField
          label="Agrupar por"
          value={filtros.agrupar}
          onChange={(e) => trocar({ agrupar: e.target.value === "municipio" ? "municipio" : "" })}
          options={[
            { value: "responsavel", label: "Organização responsável" },
            { value: "municipio", label: "Município" },
          ]}
        />
      </FilterBar>

      {/* A esteira do site tem entrada própria (`?pendentes=1`, do badge do
          menu). Dizer isso na tela evita o susto de abrir o cadastro e ver
          quatro linhas onde há duzentas. */}
      {filtros.pendentes ? (
        <div style={{ display: "flex", alignItems: "center", gap: 10, fontSize: 13, color: "var(--text-muted)" }}>
          <b style={{ color: "var(--text)" }}>Somente cadastros pendentes</b>
          <span>— vindos do site institucional, aguardando aprovação.</span>
          <Button variant="ghost" size="sm" onClick={() => trocar({ pendentes: "" })}>Ver todos</Button>
        </div>
      ) : null}

      <DataTable
        columns={columns}
        caption="Órgãos e entidades cadastrados"
        rows={filtros.agrupar === "municipio" ? agruparPorMunicipio(rows) : agruparPorResponsavel(rows, responsavelDe)}
        rowKey={(p) => String(p.id)}
        loading={data.isLoading}
        emptyState={temRecorte
          ? "Nenhum órgão ou entidade para este recorte. Limpe os filtros para ver a lista inteira."
          : "Nenhum órgão ou entidade cadastrado. Clique em “+ Novo órgão ou entidade” pra começar."}
        actions={(p) => (
          // Cadastro pendente (veio do site institucional) tem esteira própria:
          // só Aceitar/Recusar. Sincronizar folha e Desativar não fazem sentido
          // antes da aprovação — a organização ainda nem consegue logar.
          p.status === "pendente" ? (
            <AcoesCadastroPendente
              onAceitar={() => setEditing({ prefeitura: p, modo: "aprovar" })}
              onRecusar={() => setRecusando(p)}
            />
          ) : (
            <>
              <IconButton title="Abrir ficha" onClick={() => navigate(`/averbadora/organizacoes/${p.id}`)}>▤</IconButton>
              <IconButton title="Visualizar" onClick={() => setEditing({ prefeitura: p, modo: "visualizar" })}>👁</IconButton>
              <IconButton title="Editar" onClick={() => setEditing({ prefeitura: p, modo: "editar" })}>✎</IconButton>
              <IconButton title="Sincronizar folha" onClick={() => sync.mutate(p.id)}>↻</IconButton>
              {p.status === "inativo" ? (
                <IconButton title="Reativar" onClick={() => toggleAtivo.mutate(p)}>▶</IconButton>
              ) : (
                <IconButton danger title="Desativar" onClick={() => { if (confirm(`Desativar "${p.nome}"?\n\nA organização para de operar, mas os dados não são apagados — você pode reativar depois.`)) toggleAtivo.mutate(p); }}>⏸</IconButton>
              )}
            </>
          )
        )}
      />

      {editing ? (
        <OrganizacaoModal
          initial={editing.prefeitura}
          modo={editing.modo}
          todas={todas}
          onClose={() => setEditing(null)}
        />
      ) : null}
      {recusando ? (
        <ConfirmarRecusaModal
          titulo="Recusar cadastro de órgão ou entidade"
          alvo={`${recusando.nome}${recusando.uf ? `/${recusando.uf}` : ""}${recusando.cnpj ? ` — ${formatCnpj(recusando.cnpj)}` : ""}`}
          protocolo={recusando.protocoloCadastro}
          itensRemovidos={[
            "Dados da organização e credenciais de acesso",
            "Representantes legais cadastrados",
            "Responsáveis (RH, importação e financeiro)",
            "Usuário master",
          ]}
          onConfirm={(motivo) => recusar.mutateAsync({ id: recusando.id, motivo })}
          onClose={() => setRecusando(null)}
        />
      ) : null}
    </div>
  );
}
